import React, { useEffect } from 'react';
import { X, AlertTriangle, MapPin, Phone, Building2 } from 'lucide-react';
import type { DuplicateMatch } from '../../types/wizard';
import './DuplicateWarningModal.css';

interface DuplicateWarningModalProps {
  isOpen: boolean;
  matches: DuplicateMatch[];
  onEditExisting: (clinicId: number) => void;
  onContinue: () => void;
  onCancel: () => void;
}

const getStatusBadgeClass = (status: number): string => {
  switch (status) {
    case 0: return 'status-badge draft';
    case 1: return 'status-badge active';
    case 2: return 'status-badge inactive';
    case 3: return 'status-badge pending';
    default: return 'status-badge';
  }
};

const DuplicateWarningModal: React.FC<Readonly<DuplicateWarningModalProps>> = ({
  isOpen,
  matches,
  onEditExisting,
  onContinue,
  onCancel,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onCancel]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  if (!isOpen || matches.length === 0) return null;

  return (
    <div className="duplicate-modal-overlay" onClick={handleOverlayClick}>
      <div className="duplicate-modal">
        <div className="duplicate-modal-header">
          <div className="duplicate-modal-title-row">
            <span className="duplicate-icon warning">
              <AlertTriangle size={20} />
            </span>
            <h3>Potential Duplicate Found</h3>
          </div>
          <button
            className="duplicate-modal-close"
            onClick={onCancel}
            type="button"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="duplicate-modal-body">
          <p className="duplicate-description">
            We found {matches.length === 1 ? 'a clinic' : `${matches.length} clinics`} that may match
            the information you entered. Please review {matches.length === 1 ? 'it' : 'them'} to avoid
            creating a duplicate.
          </p>

          <div className="duplicate-matches-list">
            {matches.map((match) => (
              <div key={match.id} className="duplicate-match-card">
                <div className="match-header">
                  <div className="match-name-row">
                    <Building2 size={16} className="match-icon" />
                    <span className="match-name">{match.name}</span>
                  </div>
                  <span className={getStatusBadgeClass(match.status)}>
                    {match.statusDisplay}
                  </span>
                </div>

                <div className="match-details">
                  {match.address && (
                    <div className="match-detail-row">
                      <MapPin size={14} className="match-detail-icon" />
                      <span>{match.address}</span>
                    </div>
                  )}
                  {match.phone && (
                    <div className="match-detail-row">
                      <Phone size={14} className="match-detail-icon" />
                      <span>{match.phone}</span>
                    </div>
                  )}
                </div>

                <div className="match-reasons">
                  <span className="match-reasons-label">Match reasons:</span>
                  <span className="match-reasons-text">{match.matchReasons.join(', ')}</span>
                </div>

                <div className="match-score">
                  <div className="match-score-bar">
                    <div
                      className="match-score-fill"
                      style={{ width: `${Math.min(match.matchScore, 100)}%` }}
                    />
                  </div>
                  <span className="match-score-text">{match.matchScore}% match</span>
                </div>

                <button
                  type="button"
                  className="btn-edit-existing"
                  onClick={() => onEditExisting(match.id)}
                >
                  Edit "{match.name}"
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="duplicate-modal-footer">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Go Back
          </button>
          <button type="button" className="btn-primary" onClick={onContinue}>
            Not a Duplicate - Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarningModal;
